import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import {
  AI_CONVERSATION_STORAGE_KEY,
  AI_NOT_CONFIGURED_HINTS,
  AI_WELCOME_SUGGESTIONS,
  AiActionCardState,
  AiActionResult,
  AiCapabilityCategory,
  AiChatMessage,
  AiChatResponse,
  AiConfirmResponse,
  AiConfirmValues,
  AiConversationSummary,
  AiGreeting,
  AiLeadResult,
  AiPendingAction,
  AiSuggestion,
  AiTranscriptMessage,
  AiView,
} from '../models/ai-assistant.models';
import { AiAssistantApiService } from './ai-assistant-api.service';

/**
 * Single source of truth for the assistant panel: open/closed state, the visible message
 * thread, the in-flight send/confirm status, and conversation continuity.
 *
 * Provided in root so the FAB, panel, and any future entry point share one conversation.
 */
@Injectable({ providedIn: 'root' })
export class AiAssistantStateService {
  private readonly _isOpen$ = new BehaviorSubject<boolean>(false);
  readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

  private readonly _messages$ = new BehaviorSubject<AiChatMessage[]>([]);
  readonly messages$: Observable<AiChatMessage[]> = this._messages$.asObservable();

  /** True while a /chat or /confirm request is in flight — disables the composer. */
  private readonly _isBusy$ = new BehaviorSubject<boolean>(false);
  readonly isBusy$: Observable<boolean> = this._isBusy$.asObservable();

  /** Sticky banner for actionable problems (e.g. office has no AI key configured). */
  private readonly _notice$ = new BehaviorSubject<string | null>(null);
  readonly notice$: Observable<string | null> = this._notice$.asObservable();

  /** Welcome prompt chips (from `GET /suggestions`); seeded with built-in defaults. */
  private readonly _suggestions$ = new BehaviorSubject<AiSuggestion[]>(AI_WELCOME_SUGGESTIONS);
  readonly suggestions$: Observable<AiSuggestion[]> = this._suggestions$.asObservable();

  /** Capability categories for the "What can I do?" panel (from `GET /config`). */
  private readonly _capabilities$ = new BehaviorSubject<AiCapabilityCategory[]>([]);
  readonly capabilities$: Observable<AiCapabilityCategory[]> = this._capabilities$.asObservable();

  /** Timezone-aware greeting + day snapshot (from `GET /greeting`); null until first fetch. */
  private readonly _greeting$ = new BehaviorSubject<AiGreeting | null>(null);
  readonly greeting$: Observable<AiGreeting | null> = this._greeting$.asObservable();

  private conversationId: string | null = null;
  private idSeq = 0;
  private configLoaded = false;
  private greetingFetchedAt = 0;

  constructor(
    private readonly api: AiAssistantApiService,
    private readonly toastr: ToastrService
  ) {
    this.conversationId = this.readStoredConversationId();
  }

  get isOpen(): boolean {
    return this._isOpen$.value;
  }

  get messagesSnapshot(): AiChatMessage[] {
    return this._messages$.value;
  }

  open(): void {
    this._isOpen$.next(true);
    // Open the dialog on a clean slate: empty the thread (fresh welcome screen) and re-fetch the
    // greeting + day snapshot. startNewChat() clears the conversation and force-refreshes greeting.
    this.startNewChat();
  }

  close(): void {
    this._isOpen$.next(false);
  }

  toggle(): void {
    if (this._isOpen$.value) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Fetch the timezone-aware greeting (`GET /greeting`) when the panel opens. Throttled so
   * rapid toggles don't spam the endpoint, yet refreshed often enough to cross the
   * morning→afternoon→evening boundary in a long session. Non-fatal: the panel keeps its
   * instant client-side greeting if this hasn't returned (or fails).
   */
  private refreshGreeting(force = false): void {
    const FRESH_MS = 10 * 60 * 1000;
    if (!force && this._greeting$.value && Date.now() - this.greetingFetchedAt < FRESH_MS) {
      return;
    }
    this.greetingFetchedAt = Date.now();
    this.api.getGreeting().subscribe({
      next: greeting => greeting && this._greeting$.next(greeting),
      error: () => {
        /* keep the client-side greeting fallback */
      },
    });
  }

  /** Begin a fresh conversation (clears the thread and stored id). */
  startNewChat(): void {
    this.conversationId = null;
    this.clearStoredConversationId();
    this._messages$.next([]);
    this._notice$.next(null);
    // Returning to the welcome screen — re-fetch the day snapshot so the "due today /
    // overdue" badges reflect any leads actioned during the chat (bypasses the throttle).
    this.refreshGreeting(true);
  }

  // ---- Suggestions + capabilities (FRONTEND_HANDOFF §3, §8) -------------------

  /**
   * Load the welcome chips and capability categories once per session. `GET /suggestions`
   * drives the chips (enabled-only); `GET /config` drives the "What can I do?" panel.
   * Failures are non-fatal — the built-in default chips remain.
   */
  loadConfig(force = false): void {
    if (this.configLoaded && !force) {
      return;
    }
    this.configLoaded = true;

    this.api.getSuggestions().subscribe({
      next: list => {
        const enabled = (list || []).filter(s => s.enabled !== false);
        if (enabled.length) {
          this._suggestions$.next(enabled);
        }
      },
      error: () => {
        /* keep defaults */
      },
    });

    this.api.getConfig().subscribe({
      next: config => {
        if (config?.capabilities?.length) {
          this._capabilities$.next(config.capabilities);
        }
        // `/config` also carries suggested_prompts; prefer them if /suggestions was empty.
        if (config?.suggested_prompts?.length && this._suggestions$.value === AI_WELCOME_SUGGESTIONS) {
          this._suggestions$.next(config.suggested_prompts.filter(s => s.enabled !== false));
        }
      },
      error: () => {
        /* keep defaults */
      },
    });
  }

  // ---- Sending ----------------------------------------------------------------

  sendMessage(text: string, images?: string[] | null): void {
    const trimmed = (text || '').trim();
    const imgs = images?.length ? images : null;
    // Allow an image-only turn (e.g. "read this screenshot") with no typed text.
    if ((!trimmed && !imgs) || this._isBusy$.value) {
      return;
    }

    this.appendMessage({ role: 'user', content: trimmed, images: imgs });
    const placeholder = this.appendMessage({ role: 'assistant', content: '', pending: true });
    this._isBusy$.next(true);
    this._notice$.next(null);

    this.dispatchSend(trimmed, placeholder.id, imgs);
  }

  /** Re-send a failed turn (transient 502/503), reusing the same assistant bubble. */
  retryMessage(messageId: string): void {
    if (this._isBusy$.value) {
      return;
    }
    const message = this._messages$.value.find(m => m.id === messageId);
    const text = message?.retryText;
    const images = message?.retryImages ?? null;
    if (!text && !images?.length) {
      return;
    }
    this.patchMessage(messageId, { content: '', pending: true, errored: false, retryText: null, retryImages: null });
    this._isBusy$.next(true);
    this._notice$.next(null);
    this.dispatchSend(text || '', messageId, images);
  }

  private dispatchSend(text: string, placeholderId: string, images?: string[] | null): void {
    this.api
      .sendMessage(text, this.conversationId, images)
      .pipe(finalize(() => this._isBusy$.next(false)))
      .subscribe({
        next: data => this.applyChatResponse(placeholderId, data),
        error: err => this.failPlaceholder(placeholderId, err, text, images),
      });
  }

  // ---- Confirm / cancel a pending write --------------------------------------

  confirmAction(messageId: string, action: AiPendingAction, values?: AiConfirmValues): void {
    this.resolveAction(messageId, action, true, values);
  }

  cancelAction(messageId: string, action: AiPendingAction): void {
    this.resolveAction(messageId, action, false);
  }

  private resolveAction(messageId: string, action: AiPendingAction, approve: boolean, values?: AiConfirmValues): void {
    if (!this.conversationId || this._isBusy$.value) {
      return;
    }

    this.patchMessage(messageId, { actionCardState: approve ? 'confirming' : 'cancelling' });
    this._isBusy$.next(true);

    this.api
      .confirmAction(this.conversationId, action.action_id, approve, values)
      .pipe(finalize(() => this._isBusy$.next(false)))
      .subscribe({
        next: data => this.applyConfirmResponse(messageId, approve, data),
        error: err => {
          this.patchMessage(messageId, { actionCardState: 'failed' });
          this.notifyError(err);
        },
      });
  }

  // ---- History ----------------------------------------------------------------

  listConversations(): Observable<AiConversationSummary[]> {
    return this.api.listConversations();
  }

  /** Load a past conversation into the thread and resume its pending action, if any. */
  loadConversation(id: string): void {
    if (this._isBusy$.value) {
      return;
    }
    this._isBusy$.next(true);
    this._notice$.next(null);
    this.api
      .getConversation(id)
      .pipe(finalize(() => this._isBusy$.next(false)))
      .subscribe({
        next: detail => {
          this.conversationId = detail.id;
          this.storeConversationId(detail.id);
          const messages: AiChatMessage[] = (detail.messages || []).map(m => this.mapTranscriptMessage(m));
          if (detail.pending_action) {
            // Re-attach the still-open write to the last assistant bubble as an ACTIONABLE card
            // (overrides any read-only historical confirmation synthesized for that same message).
            const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
            if (lastAssistant) {
              lastAssistant.pendingAction = detail.pending_action;
              lastAssistant.actionCardState = 'pending';
            }
          }
          this._messages$.next(messages);
        },
        error: err => this.notifyError(err),
      });
  }

  /**
   * Map one persisted transcript turn into a renderable chat bubble, re-hydrating the SAME
   * structured payload it had live (Round 4): per-message `views`, `confirmation` (as a read-only
   * card) and `action_result`. Falls back to the legacy text→lead-card parsing when a turn has
   * no structured views. Content is blanked when a self-contained result list renders it (mirrors
   * {@link applyChatResponse}) so a reopened chat looks exactly like it did live.
   */
  private mapTranscriptMessage(m: AiTranscriptMessage): AiChatMessage {
    const views = m.views?.length ? m.views : null;
    const actionResult: AiActionResult | null = m.action_result ?? null;

    // A stored confirmation is a historical write proposal — render it as a read-only card.
    // Its outcome (confirmed/failed) is derived from an action_result on this or a later message;
    // absent one, it renders resolved with no terminal badge (`historical`).
    let pendingAction: AiPendingAction | null = null;
    let actionCardState: AiActionCardState | undefined;
    if (m.confirmation) {
      pendingAction = { action_id: '', tool: '', summary: '', confirmation: m.confirmation, args: {} };
      actionCardState = this.historicalCardState(actionResult);
    }

    // Legacy fallback only when no structured views arrived (old backends returned lead
    // details as a plain-text "Label: value" block).
    const parsed = !views && m.role === 'assistant' ? this.parseLeadDetails(m.content) : null;
    const content = this.rendersResultList(views) ? '' : parsed ? parsed.intro : m.content;

    return this.makeMessage({
      role: m.role,
      content,
      createdAt: this.toEpoch(m.created_at),
      views,
      leads: parsed ? [parsed.lead] : null,
      pendingAction,
      actionCardState,
      actionResult,
    });
  }

  /** Terminal state for a reloaded confirmation card, from its (optional) same-message result. */
  private historicalCardState(result: AiActionResult | null): AiActionCardState {
    if (result) {
      return result.ok === false ? 'failed' : 'confirmed';
    }
    return 'historical';
  }

  // ---- Response handling ------------------------------------------------------

  private applyChatResponse(placeholderId: string, data: AiChatResponse): void {
    if (!data) {
      this.failPlaceholder(placeholderId, null);
      return;
    }
    this.conversationId = data.conversation_id || this.conversationId;
    this.storeConversationId(this.conversationId);

    const pending = data.requires_confirmation ? (data.pending_action ?? null) : null;
    // Primary channel: structured `views[]` (FRONTEND_HANDOFF §0). Render these as cards
    // and keep `reply` as a short caption. Legacy fallbacks only when no views arrive:
    // the old `leads[]` field, then plain-text "Key: value" detail parsing.
    const views = data.views?.length ? data.views : null;
    const structuredLeads = !views && data.leads?.length ? data.leads : null;
    const parsed = views || structuredLeads ? null : this.parseLeadDetails(data.reply || '');

    this.patchMessage(placeholderId, {
      // A table/list view shows the full result itself, so don't also render the (long, duplicated)
      // prose reply above it. Legacy `leads[]` lists are likewise self-contained.
      content: this.rendersResultList(views) || structuredLeads ? '' : parsed ? parsed.intro : data.reply || '',
      pending: false,
      pendingAction: pending,
      actionCardState: pending ? 'pending' : undefined,
      // A single message can queue several writes; this is the first confirmation of `pending_count`.
      queuedRemaining: pending ? this.queuedRemaining(data.pending_count) : undefined,
      views,
      leads: structuredLeads ?? (parsed ? [parsed.lead] : null),
    });
  }

  /** True when `views` include a self-contained result list/table that makes the prose redundant. */
  private rendersResultList(views: AiView[] | null): boolean {
    return !!views && views.some(v => AiAssistantStateService.RESULT_LIST_VIEWS.has(v.type));
  }

  /** View types that render the full result (count + rows) and so replace the prose reply. */
  private static readonly RESULT_LIST_VIEWS = new Set<AiView['type']>([
    'lead_list',
    'lead_grid',
    'contact_list',
    'lead_stats',
    'appointment_list',
    'my_appointment_list',
    'lead_views',
    'day_briefing',
    'project_list',
    'lot_list',
    'eoi_list',
    'project_task_list',
    'project_event_list',
  ]);

  /** Writes still awaiting confirmation after the one currently shown (0 when single-action). */
  private queuedRemaining(pendingCount?: number): number {
    return pendingCount && pendingCount > 1 ? pendingCount - 1 : 0;
  }

  private applyConfirmResponse(messageId: string, approved: boolean, data: AiConfirmResponse): void {
    const result = data?.action_result ?? null;
    const cancelled = !approved || data?.cancelled === true;
    const succeeded = !cancelled && (result?.ok ?? true);

    this.patchMessage(messageId, {
      pendingAction: null,
      actionResult: result,
      actionCardState: cancelled ? 'cancelled' : succeeded ? 'confirmed' : 'failed',
    });

    if (data?.conversation_id) {
      this.conversationId = data.conversation_id;
      this.storeConversationId(this.conversationId);
    }

    // When a write fails only because required fields are missing, the original message now
    // renders inline dropdowns for them — don't also append the redundant "I couldn't complete
    // that: … Please provide: …" text bubble.
    const needsFields = !!result && result.ok === false && (result.required_fields?.length ?? 0) > 0;

    // 1) Render this action's completed result (card + any error text). Skipped for the
    //    missing-fields case — the original message already shows inline dropdowns.
    const views = data?.views?.length ? data.views : null;
    if (!needsFields && (data?.reply || views)) {
      this.appendMessage({ role: 'assistant', content: data?.reply || '', views });
    }

    // 2) Multi-action queue: a confirm response can carry the NEXT queued write. Render it as a
    //    fresh confirmation card so the user approves/cancels each one in turn (a cancel also
    //    advances the queue). No extra request — the next action rides in on this same response.
    if (data?.requires_confirmation && data.pending_action) {
      this.appendMessage({
        role: 'assistant',
        content: '',
        pendingAction: data.pending_action,
        actionCardState: 'pending',
        queuedRemaining: this.queuedRemaining(data.pending_count),
      });
    }
  }

  private failPlaceholder(placeholderId: string, err: unknown, retryText?: string, retryImages?: string[] | null): void {
    const message = this.extractErrorMessage(err);
    const status = this.errorStatus(err);

    // 422 (or a "not configured" message): misconfiguration won't fix itself on retry —
    // show the sticky settings hint and no Retry button (FRONTEND_HANDOFF §2).
    if (status === 422 || this.isNotConfigured(message)) {
      this._notice$.next('AI is not set up for this office yet. Please contact your administrator.');
      this.patchMessage(placeholderId, {
        content: 'AI is not set up for this office yet — please contact your administrator.',
        pending: false,
        errored: true,
        retryText: null,
        retryImages: null,
      });
      return;
    }

    // 503 "AI service is busy" / 502 "AI service error" are transient — offer Retry
    // with clear copy (FRONTEND_HANDOFF §2). Backend already retried/fell back internally.
    if (status === 503 || status === 502) {
      this.patchMessage(placeholderId, {
        content:
          status === 503 ? 'The AI service is busy right now. Please try again in a moment.' : 'The AI service hit an error. Please try again.',
        pending: false,
        errored: true,
        retryText: retryText ?? null,
        retryImages: retryImages ?? null,
      });
      return;
    }

    this.patchMessage(placeholderId, {
      content: message || 'Something went wrong. Please try again.',
      pending: false,
      errored: true,
      retryText: retryText ?? null,
      retryImages: retryImages ?? null,
    });
  }

  private notifyError(err: unknown): void {
    this.toastr.error(this.extractErrorMessage(err) || 'Something went wrong. Please try again.');
  }

  // ---- Message helpers --------------------------------------------------------

  private appendMessage(partial: Partial<AiChatMessage> & { role: AiChatMessage['role']; content: string }): AiChatMessage {
    const message = this.makeMessage(partial);
    this._messages$.next([...this._messages$.value, message]);
    return message;
  }

  private makeMessage(partial: Partial<AiChatMessage> & { role: AiChatMessage['role']; content: string }): AiChatMessage {
    return {
      id: `m_${Date.now().toString(36)}_${this.idSeq++}`,
      role: partial.role,
      content: partial.content,
      createdAt: partial.createdAt ?? Date.now(),
      pending: partial.pending ?? false,
      errored: partial.errored ?? false,
      retryText: partial.retryText ?? null,
      retryImages: partial.retryImages ?? null,
      images: partial.images ?? null,
      pendingAction: partial.pendingAction ?? null,
      actionCardState: partial.actionCardState,
      queuedRemaining: partial.queuedRemaining,
      actionResult: partial.actionResult ?? null,
      leads: partial.leads ?? null,
      views: partial.views ?? null,
    };
  }

  private patchMessage(id: string, patch: Partial<AiChatMessage>): void {
    this._messages$.next(this._messages$.value.map(m => (m.id === id ? { ...m, ...patch } : m)));
  }

  // ---- Lead-details text parsing (fallback until BE-1) ------------------------

  /**
   * The backend currently returns lead details as a plain-text labelled block, e.g.:
   *
   *   Here are the details for Andy Taylor:
   *   Email: andy@benrock.com.au
   *   Lead Source: Google search
   *   Lead Status: No Further Interest
   *   Lead Type: Buyer
   *   Phone: +61456799923
   *   Temperature: Cold
   *
   * When a reply matches that shape (≥3 recognised "Label: value" lines), parse it into a
   * lead so it renders as a card. Returns the intro line + the lead, or null on no match.
   * Strict on purpose — prose replies ("his email is …") are left as text.
   */
  private parseLeadDetails(reply: string): { intro: string; lead: AiLeadResult } | null {
    if (!reply || reply.indexOf(':') === -1) {
      return null;
    }

    const fieldByLabel: Record<string, keyof AiLeadResult> = {
      email: 'email',
      phone: 'phone',
      mobile: 'phone',
      'mobile phone': 'phone',
      source: 'source',
      'lead source': 'source',
      status: 'status',
      'lead status': 'status',
      type: 'type',
      'lead type': 'type',
      temperature: 'temperature',
      agent: 'agent',
      'assigned agent': 'agent',
      project: 'project',
      name: 'name',
    };

    const kvLine = /^\s*([A-Za-z][A-Za-z \-/]*?)\s*:\s*(.+\S)\s*$/;
    const lines = reply.split(/\r?\n/);
    const lead: AiLeadResult = { id: '' };
    let matched = 0;
    let firstKvIndex = -1;

    lines.forEach((line, i) => {
      const m = line.match(kvLine);
      if (!m) {
        return;
      }
      if (firstKvIndex === -1) {
        firstKvIndex = i;
      }
      const field = fieldByLabel[m[1].trim().toLowerCase()];
      if (field) {
        (lead as unknown as Record<string, string>)[field] = m[2].trim();
        matched++;
      }
    });

    if (matched < 3) {
      return null;
    }

    if (!lead.name) {
      const nameMatch = reply.match(/details (?:for|of)\s+(.+?)\s*[:.\n]/i);
      if (nameMatch) {
        lead.name = nameMatch[1].trim();
      }
    }
    // Capture an id if the reply happens to mention one (so "Open Lead" deep-links).
    const idMatch = reply.match(/\bID[\s:'"]*([A-Za-z0-9]{16,})/);
    if (idMatch) {
      lead.id = idMatch[1];
    }

    const intro = (firstKvIndex > 0 ? lines.slice(0, firstKvIndex).join('\n') : '').trim();
    return { intro, lead };
  }

  // ---- Misc -------------------------------------------------------------------

  private extractErrorMessage(err: unknown): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    const anyErr = err as any;
    return anyErr?.error?.message || anyErr?.message || anyErr?.error || '';
  }

  /** HTTP status code from an HttpErrorResponse-shaped error, or 0 if unknown. */
  private errorStatus(err: unknown): number {
    const status = (err as any)?.status;
    return typeof status === 'number' ? status : 0;
  }

  private isNotConfigured(message: string): boolean {
    const lower = (message || '').toLowerCase();
    return AI_NOT_CONFIGURED_HINTS.some(hint => lower.includes(hint));
  }

  private toEpoch(iso: string): number {
    const parsed = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  private readStoredConversationId(): string | null {
    try {
      return sessionStorage.getItem(AI_CONVERSATION_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeConversationId(id: string | null): void {
    try {
      if (id) sessionStorage.setItem(AI_CONVERSATION_STORAGE_KEY, id);
    } catch {
      /* sessionStorage unavailable (private mode) — continuity is best-effort */
    }
  }

  private clearStoredConversationId(): void {
    try {
      sessionStorage.removeItem(AI_CONVERSATION_STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
